import { create, StoreApi, UseBoundStore } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { LocationPrecision, LocationStore } from './locationStore.types';

// Default search radius: 15 km (mirrors the mobile apps' location store default),
// so an IP-based position alone already yields nearby results.
export const DEFAULT_LOCATION_DISTANCE = 15000;

/** 'GPS' ⇄ 'browser', everything else ⇄ 'ip' — keeps the deprecated locationLevel in sync. */
function precisionToLocationLevel(precision: LocationPrecision): 'ip' | 'browser' {
    return precision === 'GPS' ? 'browser' : 'ip';
}

/**
 * The geo/city API serializes its decimal lat/lon columns as strings, and several setters
 * (e.g. city autocomplete) forward `CityDto.longitude/latitude` verbatim despite the DTO
 * typing them as `number`. Coerce here so the store's `number | undefined` invariant actually
 * holds — consumers like `useNearbyCinemas` call `.toFixed()` on these and crash on a string.
 */
function toCoordinate(value: number | string | undefined | null): number | undefined {
    if (value == null || value === '') return undefined;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
}

const useLocationStore: UseBoundStore<StoreApi<LocationStore>> = create(
    persist<LocationStore>(
        (set, currentState) => ({
            longitude: undefined,
            latitude: undefined,
            distance: DEFAULT_LOCATION_DISTANCE, // meters
            precision: 'IP',
            locationLevel: 'ip',
            geoLocation: undefined,
            isLocationInitialized: false,

            setGeoLocation: (geoLocation) => set(() => ({ geoLocation })),
            setLocation: (longitude, latitude) =>
                set(() => ({ longitude: toCoordinate(longitude), latitude: toCoordinate(latitude) })),
            setDistance: (radius) => set(() => ({ distance: radius })),
            unsetLocation: () =>
                set(() => ({
                    longitude: undefined,
                    latitude: undefined,
                    distance: DEFAULT_LOCATION_DISTANCE,
                    precision: 'IP',
                    locationLevel: 'ip'
                })),
            setPrecision: (precision: LocationPrecision) =>
                set(() => ({ precision, locationLevel: precisionToLocationLevel(precision) })),
            setLocationLevel: (level: undefined | 'ip' | 'browser') =>
                set((state) => ({
                    locationLevel: !level ? 'ip' : level,
                    // 'browser' means the browser's geolocation is granted/used → GPS precision.
                    // 'ip' only downgrades when the current precision is not MANUAL.
                    precision: level === 'browser' ? 'GPS' : state.precision === 'MANUAL' ? state.precision : 'IP'
                })),
            setLocationInitialized: (initialized) => set(() => ({ isLocationInitialized: initialized })),
            isAllLocationDetailsAvailable: () => {
                return (
                    !Number.isNaN(Number(currentState().longitude)) && !Number.isNaN(Number(currentState().latitude))
                );
            }
        }),
        {
            name: 'location-store',
            storage: createJSONStorage(() => localStorage),
            // Version 1: introduces `precision` (IP/GPS/MANUAL) and the 15 km default distance.
            // Version 2: normalizes coordinates persisted as strings (see `toCoordinate`) — older
            // clients stored the API's string lat/lon, which crashed `.toFixed()` consumers.
            version: 2,
            migrate: (persistedState, version) => {
                const state = persistedState as LocationStore;
                if (version < 1) {
                    state.precision = state.locationLevel === 'browser' ? 'GPS' : 'IP';
                    if (!state.distance || state.distance <= 0) {
                        state.distance = DEFAULT_LOCATION_DISTANCE;
                    }
                }
                if (version < 2) {
                    state.longitude = toCoordinate(state.longitude);
                    state.latitude = toCoordinate(state.latitude);
                }
                return state;
            }
        }
    )
);

export default useLocationStore;
